import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GiFairyWings } from "react-icons/gi";
import { 
  FaImages, FaArrowLeft, FaTimes, FaVideo, 
  FaBookOpen, FaFilePdf, FaPlay, FaImage, FaExpand
} from "react-icons/fa";
import "./Gallery.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5000";

// Helper to extract YouTube Video ID for thumbnails
const getYouTubeID = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/gallery/public`);
        const data = await response.json();

        // 3 Strict Predefined Categories
        const grouped = {
          PHOTOS: { id: 'PHOTOS', title: 'PHOTOS', icon: <FaImages />, items: [] },
          VIDEOS: { id: 'VIDEOS', title: 'VIDEOS', icon: <FaVideo />, items: [] },
          BOOKS: { id: 'BOOKS', title: 'BOOKS', icon: <FaBookOpen />, items: [] }
        };

        // Populate items into their respective folders
        data.forEach(item => {
          if (grouped[item.folderName]) {
            grouped[item.folderName].items.push(item);
          }
        });

        setAlbums(Object.values(grouped));
      } catch (error) {
        console.error("Failed to fetch gallery data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedPhoto ? "hidden" : "auto";
  }, [selectedPhoto]);

  const handleCategoryClick = (category) => {
    if (category.items.length > 0) {
      setActiveCategory(category);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBackToAlbums = () => setActiveCategory(null);
  const handlePhotoClick = (imgUrl, title) => setSelectedPhoto({ url: imgUrl, title });
  const handleCloseLightbox = () => setSelectedPhoto(null);

  // Helper to get correct thumbnail image depending on folder type
  const getThumbnail = (item, folderId) => {
    if (!item) return null;
    if (folderId === 'VIDEOS') {
      const ytId = getYouTubeID(item.ytLink);
      return ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null;
    }
    return item.image ? `${API_BASE_URL}${item.image}` : null;
  };

  if (loading) {
    return <div className="gallary-page-loading">Loading Gallery...</div>;
  }

  return (
    <>
      <Helmet>
        <title>Gallery | Rajput Shiksha Kosh</title>
        <meta
          name="description"
          content="Explore our gallery to see the latest photos and videos from Rajput Shiksha Kosh."
        />
      </Helmet>

      <div className="gallary-page-wrapper">
        <div className="gallary-page-container">

          {/* HEADER (No Breadcrumbs, News Theme) */}
          <div className="gallary-page-header">
            <h1 className="gallary-page-title">
              Our Gallery
            </h1>

            <div className="gallary-page-divider">
              <span className="gallary-page-line"></span>
              <GiFairyWings className="gallary-page-icon" />
              <span className="gallary-page-line"></span>
            </div>
          </div>

          {/* FILTER / CONTROL BAR */}
          <div className="gallary-page-filter-wrapper">
            <div className="gallary-page-filter-bar">
              <div className="gallary-page-filter-left">
                {!activeCategory ? "Gallery Collections" : activeCategory.title}
              </div>

              <div className="gallary-page-filter-right">
                {activeCategory ? (
                  <button className="gallary-page-back-btn" onClick={handleBackToAlbums}>
                    <FaArrowLeft /> Back to Collections
                  </button>
                ) : (
                  <div className="gallary-page-meta-info">
                    Total Collections: {albums.length}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* GRID */}
          <div className="gallary-page-grid">
            
            {!activeCategory ? (
              
              /* --- SHOW FOLDERS / ALBUMS --- */
              albums.map((album) => {
                const mainThumb = getThumbnail(album.items[0], album.id);
                return (
                  <div 
                    key={album.id} 
                    className={`gallary-page-card folder-card ${album.items.length === 0 ? 'empty' : ''}`}
                    onClick={() => handleCategoryClick(album)}
                  >
                    <div className="gallary-page-image-wrapper">
                      {mainThumb ? (
                        <img src={mainThumb} alt={album.title} className="gallary-page-image" />
                      ) : (
                        <div className="gallary-page-no-image">
                          {album.icon}
                          <span>Empty Folder</span>
                        </div>
                      )}
                    </div>

                    <div className="gallary-page-content">
                      <h2 className="gallary-page-card-title">{album.title}</h2>
                      <div className="gallary-page-date">
                        {album.icon} {album.items.length} Items
                      </div>
                    </div>
                  </div>
                );
              })

            ) : (

              /* --- SHOW ITEMS INSIDE SELECTED FOLDER --- */
              activeCategory.items.length > 0 ? (
                activeCategory.items.map((item, index) => {

                  // 1. PHOTO ITEM
                  if (activeCategory.id === 'PHOTOS') {
                    const imgUrl = `${API_BASE_URL}${item.image}`;
                    return (
                      <div key={index} className="gallary-page-card" onClick={() => handlePhotoClick(imgUrl, item.title)} style={{cursor: 'pointer'}}>
                        <div className="gallary-page-image-wrapper">
                          <img src={imgUrl} alt={item.title} className="gallary-page-image" />
                        </div>
                        <div className="gallary-page-content">
                          <h2 className="gallary-page-card-title">{item.title}</h2>
                          <div className="gallary-page-date"><FaExpand /> Click to View Photo</div>
                        </div>
                      </div>
                    );
                  }

                  // 2. VIDEO ITEM
                  if (activeCategory.id === 'VIDEOS') {
                    const ytId = getYouTubeID(item.ytLink);
                    const thumbUrl = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null;
                    return (
                      <a key={index} href={item.ytLink} target="_blank" rel="noreferrer" className="gallary-page-card">
                        <div className="gallary-page-image-wrapper">
                           {thumbUrl ? (
                              <img src={thumbUrl} alt={item.title} className="gallary-page-image" />
                           ) : (
                              <div className="gallary-page-no-image"><FaVideo /><span>No Thumbnail</span></div>
                           )}
                        </div>
                        <div className="gallary-page-content">
                          <h2 className="gallary-page-card-title">{item.title}</h2>
                          <div className="gallary-page-date"><FaPlay /> Watch Video</div>
                        </div>
                      </a>
                    );
                  }

                  // 3. BOOK ITEM
                  if (activeCategory.id === 'BOOKS') {
                    const imgUrl = item.image ? `${API_BASE_URL}${item.image}` : null;
                    const pdfUrl = `${API_BASE_URL}${item.pdf}`;
                    return (
                      <a key={index} href={pdfUrl} target="_blank" rel="noreferrer" className="gallary-page-card">
                        <div className="gallary-page-image-wrapper" style={{background: '#eaeaea'}}>
                           {imgUrl ? (
                              <img src={imgUrl} alt={item.title} className="gallary-page-image" style={{objectFit: 'contain'}} />
                           ) : (
                              <div className="gallary-page-no-image"><FaBookOpen /><span>No Cover</span></div>
                           )}
                        </div>
                        <div className="gallary-page-content">
                          <h2 className="gallary-page-card-title">{item.title}</h2>
                          <div className="gallary-page-date"><FaFilePdf /> Read PDF Book</div>
                        </div>
                      </a>
                    );
                  }

                  return null;
                })
              ) : (
                <div className="gallary-page-empty">No items found in this collection.</div>
              )
            )}
          </div>

        </div>

        {/* LIGHTBOX FOR PHOTOS */}
        {selectedPhoto && (
          <div className="gallary-page-lightbox" onClick={handleCloseLightbox}>
            <div className="gallary-page-lightbox-content" onClick={(e) => e.stopPropagation()}>
              <div className="gallary-page-lightbox-image-container">
                <img src={selectedPhoto.url} alt="Full View" />
                <button className="gallary-page-close-btn" onClick={handleCloseLightbox}><FaTimes /></button>
              </div>
              <div className="gallary-page-lightbox-desc">
                <p>{selectedPhoto.title}</p>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default Gallery;